import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Skeleton } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';

import { Button } from '@ui-library/button';
import { AccountFieldProps } from '@components/common/types';
import { AccountInfo } from '@components/retlWorkflow/components/setupSource/credentials/accountInfo';
import useStores from '@stores/useStores';
import { UiConfig } from '@stores/sourceDefinitionsList';
import { canEditSource } from '@hooks/useActionGuard/utils/permissionChecks';
import { assertNonNullish } from '@utils/assert';

import CredentialPopup from './credential-popup';
import { ConfigurationSettingCard } from '../../../retlSourceConfiguration/common/configurationSettingRow';

export const RetlSourceAccountManagement: React.FC = observer(() => {
  const { sourceDataStore, retlSourceListStore } = useStores();
  const [showCredentialPopup, setShowCredentialPopup] = useState(false);
  const [isAccountLoading, setIsAccountLoading] = useState(false);

  const getCredentialInfo = async () => {
    setIsAccountLoading(true);
    await sourceDataStore.getCredentialInfo(true);
    setIsAccountLoading(false);
  };

  useEffect(() => {
    getCredentialInfo();
  }, []);

  let modal = null;

  if (showCredentialPopup) {
    modal = <CredentialPopup onCancel={() => setShowCredentialPopup(false)} />;
  }

  const accountSummaryFields =
    ((sourceDataStore.sourceDef?.uiConfig as UiConfig[])[0]?.fields?.filter(
      (field) => field.addInAccountSummary,
    ) as AccountFieldProps[]) || [];

  const editSourcePermission = canEditSource(sourceDataStore.id ?? '');

  const retlSource = retlSourceListStore.getRetlSourceById(sourceDataStore.id);
  assertNonNullish(retlSource, 'Retl source not found');

  const canEditCredentials = retlSource.isProfilesSource
    ? {
        isAllowed: false,
        denialReason: 'You cannot edit credentials for sources created via profiles workflows.',
      }
    : editSourcePermission;

  return (
    <ConfigurationSettingCard cardTitle="Warehouse account">
      <Skeleton title={{ width: 300 }} loading={isAccountLoading} active>
        {sourceDataStore.accountInfo && (
          <>
            {modal}

            <AccountInfo
              fields={accountSummaryFields}
              accountName={sourceDataStore.accountInfo.name}
              info={{
                id: sourceDataStore.accountInfo.id,
                ...sourceDataStore.accountInfo?.options,
                createdAt: sourceDataStore.accountInfo.createdAt,
              }}
            />

            <Button
              data-testid="edit-button"
              onClick={() => setShowCredentialPopup(true)}
              type="default"
              size="middle"
              icon={<FontAwesomeIcon icon={regular('pencil')} />}
              disabled={!canEditCredentials.isAllowed}
              tooltip={{ title: canEditCredentials.denialReason }}
              className="m-l-auto"
            />
          </>
        )}
      </Skeleton>
    </ConfigurationSettingCard>
  );
});
